import { Pipe, PipeTransform, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import dayjs, { type Dayjs } from 'dayjs';
import CustomParseFormat from 'dayjs/plugin/customParseFormat';

@Pipe({
  name: 'localeDate',
})
export class BackendLocaleDatePipe implements PipeTransform {
  private readonly currentLang = inject(TranslateService).getCurrentLang();

  private readonly localeFormat: string;
  private readonly localeFormatTime: string;
  private readonly locale: string;

  constructor() {
    dayjs.extend(CustomParseFormat);
    this.localeFormat = dayjs.Ls[this.currentLang]?.formats?.['L'] ?? 'MM/DD/YYYY';
    this.localeFormatTime = dayjs.Ls[this.currentLang]?.formats?.['LTS'] ?? 'hh:mm:ss';
    this.locale = this.currentLang ?? 'de';
  }

  /** für Initialisierung von clrDate Input
   * @example new UntypedFormControl(GetLocaleDatePipe.now)
   */
  public static get now(): string {
    return dayjs().format(BackendLocaleDatePipe.format);
  }

  /** für Initialisierung von clrDate Input
   * @example new UntypedFormControl(GetLocaleDatePipe.now)
   */
  public static get nowtime(): string {
    return dayjs().format('HH:mm');
  }

  /** für Initialisierung von clrDate Input
   * @example new UntypedFormControl(GetLocaleDatePipe.today)
   */
  public static get today(): string {
    return dayjs().format(BackendLocaleDatePipe.formatDate);
  }

  /** für DateiNamen
   * @example name = `${GetLocaleDatePipe.timestamp}.png`
   */
  public static get timestamp(): string {
    return dayjs().format('YYYY-MM-DDTHH-mm-ss.SSS');
  }

  /** für schnelle Zugriff auf BackEnd DateTimeFormat
   * @example dayjs().add(2, 'days').format(GetLocaleDatePipe.format)
   */
  public static get format(): string {
    return 'YYYY-MM-DDTHH:mm:ss';
  }

  /** für schnelle Zugriff auf BackEnd OnlyDateFormat
   * @example dayjs().add(2, 'days').format(GetLocaleDatePipe.formatDate)
   */
  public static get formatDate(): string {
    return 'YYYY-MM-DDT00:00:00';
  }

  /** nur für ANZEIGE
   * @param value 'YYYY-MM-DDTHH:mm:ss'
   * @returns en: 'MM/DD/YYYY'; de, ru: 'DD.MM.YYYY'; andere: 'DD/MM/YYYY'
   * @example [ngModel]="formGroup.controls['Datum'].value | localeDate"
   * @example [ngModel]="datum | localeDate"
   */
  transform(value: string): string {
    return value ? dayjs(value).format(this.localeFormat) : null;
  }
  transformTime(value: string): string {
    return value ? dayjs(value).format(this.localeFormatTime) : null;
  }

  /**
   * für Speichern in BackEnd
   * @alias tosql
   * @see tosql()
   * @param value en: 'MM/DD/YYYY'; de, ru: 'DD.MM.YYYY'; andere: 'DD/MM/YYYY'
   * @returns 'YYYY-MM-DDTHH:mm:ss'
   */
  toBackEndFormat(value: string): string {
    if (!value) return null;
    const date = dayjs(value, this.localeFormat);

    return date.isValid() ? date.format(BackendLocaleDatePipe.format) : null;
  }

  /**
   * @see toBackEndFormat()
   * @example (ngModelChange)="datum = localeDate.tosql($event)"
   * @example (ngModelChange)="formGroup.controls['Datum'].setValue(localeDate.tosql($event))"
   */
  tosql = this.toBackEndFormat;

  /**
   * @description Schreibt Zeit zu Datum string
   * @param date 'YYYY-MM-DDTHH:mm:ss'
   * @param time 'HH:mm:ss' | 'HH:mm'
   * @returns 'YYYY-MM-DDTHH:mm:ss' | null
   */
  addTimeToDate(date: string, time: string) {
    if (!date) return null;
    const [h, m] = (time || '0:0').split(':');
    return dayjs(date).hour(Number(h)).minute(Number(m)).second(0).format(BackendLocaleDatePipe.format);
  }

  formatInLocale(date: Dayjs, format: string): string {
    date = date.locale(this.locale);
    return date.format(format);
  }

  toDateFromBackend(value: string) {
    if (!value) return null;
    const date = dayjs(value);
    return date.add(date.utcOffset(), 'minutes').toDate();
  }
}
